import React, { useState, useEffect } from 'react';
import api from '../axiosConfig'; 
import Dashboard from './Dashboard';
import OpportunitiesList from './OpportunitiesList';
import StylistPerformance from './StylistPerformance';
import PatternAnalysis from './PatternAnalysis';
import MBComparison from './MBComparison';
import ScenarioBuilder from './ScenarioBuilder';
import './Efficiency.css';

console.log("EFFICIENCY INDEX LOADED");

const EfficiencyAnalyzer = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [timeRange, setTimeRange] = useState(30);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Data states
  const [overview, setOverview] = useState(null);
  const [opportunities, setOpportunities] = useState(null);
  const [stylists, setStylists] = useState(null);
  const [patterns, setPatterns] = useState(null);
  const [mbComparison, setMbComparison] = useState(null);

  useEffect(() => {
    loadAllData();
  }, [timeRange]);

  const loadAllData = async () => {
    setLoading(true);
    setError(null);

    try {
      // Fetch all data in parallel
      const [overviewRes, opportunitiesRes, stylistsRes, patternsRes, mbRes] = 
        await Promise.all([
          api.get(`/efficiency/overview?days=${timeRange}`),
          api.get(`/efficiency/opportunities?days=${timeRange}`),
          api.get(`/efficiency/stylists?days=${timeRange}`),
          api.get(`/efficiency/patterns?days=${timeRange}`),
          api.get(`/efficiency/mb-comparison?days=${timeRange}`)
        ]);

      const overviewData = overviewRes.data;
      const opportunitiesData = opportunitiesRes.data;
      const stylistsData = stylistsRes.data;
      const patternsData = patternsRes.data;
      const mbData = mbRes.data;

      // Set data - handle both wrapped (.data) and unwrapped responses
      setOverview(overviewData.data || overviewData);
      setOpportunities(opportunitiesData.data || opportunitiesData);
      setStylists(stylistsData.data || stylistsData);
      setPatterns(patternsData.data || patternsData);
      setMbComparison(mbData.data || mbData);
    } catch (err) {
      setError(err.message);
      console.error('Error loading efficiency data:', err);
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'dashboard', label: 'Overview', icon: '📊' },
    { id: 'opportunities', label: 'Opportunities', icon: '💡' },
    { id: 'stylists', label: 'Team Performance', icon: '👥' },
    { id: 'patterns', label: 'Patterns', icon: '📈' },
    { id: 'mb-comparison', label: 'M&B Benchmark', icon: '⚖️' },
    { id: 'scenario', label: 'Scenario Builder', icon: '🎯' }
  ];

  if (loading) {
    return (
      <div className="efficiency-analyzer-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Analysing your salon efficiency...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="efficiency-analyzer-container">
        <div className="error-state">
          <p>Error loading efficiency data: {error}</p>
          <button onClick={() => window.location.reload()}>Retry</button>
      </div>
      </div>
    );
  }

  return (
    <div className="efficiency-analyzer-container">
      {/* Header */}
      <div className="efficiency-header">
        <div className="header-content">
          <h1>Business Efficiency Analyser</h1>
          <p className="efficiency-subtitle">
            Evidence-based insights from your actual performance data. 
            No VAT cut needed. No lobbying required. No waiting.
          </p>
        </div>

        {/* Time Range Selector */}
        <div className="time-range-selector">
          <label>Analysis Period:</label>
          <select 
            value={timeRange} 
            onChange={(e) => setTimeRange(Number(e.target.value))}
            className="time-select"
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            <option value={180}>Last 6 months</option>
            <option value={365}>Last year</option>
          </select>
        </div>
      </div>

      {/* Quick Stats Banner */}
      {overview && (
        <div className="quick-stats-banner">
          <div className="stat-card">
            <div className="stat-value">
              {overview.overall_score?.toFixed(0) || overview.efficiency_score || 0}/100
            </div>
            <div className="stat-label">Efficiency Score</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">
              £{(opportunities?.total_annual_opportunity || 
                 opportunities?.summary?.total_annual_value || 0).toLocaleString()}
            </div>
            <div className="stat-label">Annual Opportunity</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">
              {opportunities?.opportunities?.length || 
               opportunities?.summary?.total_opportunities || 0}
            </div>
            <div className="stat-label">Action Items</div>
          </div>
          <div className="stat-card highlight">
            <div className="stat-value">
              {(mbComparison?.cost_coverage_ratio || 
                mbComparison?.mb_approach_for_you?.coverage_percentage || 0).toFixed(1)}×
            </div>
            <div className="stat-label">Cost Coverage Ratio</div>
          </div>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="tab-navigation">
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            <span className="tab-icon">{tab.icon}</span>
            <span className="tab-label">{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {activeTab === 'dashboard' && (
          <Dashboard 
            data={overview}
            opportunities={opportunities}
            days={timeRange}
          />
        )}
        
        {activeTab === 'opportunities' && (
          <OpportunitiesList 
            data={opportunities}
            days={timeRange}
          />
        )}
        
        {activeTab === 'stylists' && (
          <StylistPerformance 
            data={stylists}
            days={timeRange}
          />
        )}
        
        {activeTab === 'patterns' && (
          <PatternAnalysis 
            data={patterns}
            days={timeRange}
          />
        )}
        
        {activeTab === 'mb-comparison' && (
          <MBComparison 
            data={mbComparison}
            days={timeRange}
          />
        )}
        
        {activeTab === 'scenario' && (
          <ScenarioBuilder 
            baseData={overview}
            days={timeRange}
            onScenarioChange={(data) => console.log('Scenario changed:', data)}
          />
        )}
      </div>

      {/* Footer */}
      <div className="efficiency-footer">
        <p>
          <strong>All data from your actual employee_income records.</strong> This isn't theory or 
          industry averages - this is YOUR salon's real performance, mathematically analysed.
        </p>
      </div>
    </div>
  );
};

export default EfficiencyAnalyzer;